import {TransientJobStatus, TransientJob, TransientSeriesResponse} from '@crema/services/apis/ctdTransient';
import {AppActions} from '../../types';
import {
  CTD_TRANSIENT_UPLOAD_START,
  CTD_TRANSIENT_UPLOAD_SUCCESS,
  CTD_TRANSIENT_UPLOAD_FAILURE,
  CTD_TRANSIENT_POLL_UPDATE,
  CTD_TRANSIENT_POLL_STOP,
  CTD_TRANSIENT_FETCH_HISTORY_START,
  CTD_TRANSIENT_FETCH_HISTORY_SUCCESS,
  CTD_TRANSIENT_FETCH_HISTORY_FAILURE,
  CTD_TRANSIENT_CLEAR_CURRENT_JOB,
  CTD_TRANSIENT_SERIES_FETCH_START,
  CTD_TRANSIENT_SERIES_FETCH_SUCCESS,
  CTD_TRANSIENT_SERIES_FETCH_FAILURE,
} from '../../types/actions/CTDTransient.actions';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

interface CTDTransientState {
  /** The job currently being tracked (null until the user submits a file). */
  currentJob: TransientJobStatus | null;
  /** True while the polling interval is expected to be running. */
  isPolling: boolean;
  /** All transient parse jobs belonging to the authenticated user. */
  jobHistory: TransientJob[];
  /** True while the upload POST request is in flight. */
  isUploading: boolean;
  /** True while the job-history GET request is in flight. */
  isFetchingHistory: boolean;
  /** The currently loaded node series (null until a node is selected). */
  series: TransientSeriesResponse | null;
  /** True while the node-series GET request is in flight. */
  isFetchingSeries: boolean;
  /** Module-level error message (null when there is no error). */
  error: string | null;
}

const INIT_STATE: CTDTransientState = {
  currentJob: null,
  isPolling: false,
  jobHistory: [],
  isUploading: false,
  isFetchingHistory: false,
  series: null,
  isFetchingSeries: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const CTDTransient = (state: CTDTransientState = INIT_STATE, action: AppActions): CTDTransientState => {
  switch (action.type) {
    // --- Upload lifecycle ---
    case CTD_TRANSIENT_UPLOAD_START: {
      return {
        ...state,
        isUploading: true,
        error: null,
      };
    }

    case CTD_TRANSIENT_UPLOAD_SUCCESS: {
      return {
        ...state,
        isUploading: false,
        isPolling: true,
        // Seed currentJob with the initial status so the polling component
        // renders immediately without waiting for the first poll tick.
        currentJob: {
          job_id: action.payload.jobId,
          status: action.payload.status as TransientJobStatus['status'],
        } as TransientJobStatus,
        series: null,
        error: null,
      };
    }

    case CTD_TRANSIENT_UPLOAD_FAILURE: {
      return {
        ...state,
        isUploading: false,
        error: action.error,
      };
    }

    // --- Polling ---
    case CTD_TRANSIENT_POLL_UPDATE: {
      const pollable =
        action.payload.status === 'pending' ||
        action.payload.status === 'running';

      return {
        ...state,
        currentJob: action.payload,
        isPolling: pollable,
      };
    }

    case CTD_TRANSIENT_POLL_STOP: {
      return {
        ...state,
        isPolling: false,
      };
    }

    // --- Node series ---
    case CTD_TRANSIENT_SERIES_FETCH_START: {
      return {
        ...state,
        isFetchingSeries: true,
      };
    }

    case CTD_TRANSIENT_SERIES_FETCH_SUCCESS: {
      return {
        ...state,
        isFetchingSeries: false,
        series: action.payload,
      };
    }

    case CTD_TRANSIENT_SERIES_FETCH_FAILURE: {
      return {
        ...state,
        isFetchingSeries: false,
        error: action.error,
      };
    }

    // --- History ---
    case CTD_TRANSIENT_FETCH_HISTORY_START: {
      return {
        ...state,
        isFetchingHistory: true,
      };
    }

    case CTD_TRANSIENT_FETCH_HISTORY_SUCCESS: {
      return {
        ...state,
        isFetchingHistory: false,
        jobHistory: action.payload,
      };
    }

    case CTD_TRANSIENT_FETCH_HISTORY_FAILURE: {
      return {
        ...state,
        isFetchingHistory: false,
        error: action.error,
      };
    }

    // --- Reset ---
    case CTD_TRANSIENT_CLEAR_CURRENT_JOB: {
      return {
        ...state,
        currentJob: null,
        isPolling: false,
        series: null,
        error: null,
      };
    }

    default:
      return state;
  }
};

export default CTDTransient;
